import React from 'react';
import './RandomizerForm.css';
import Flex from '../Flex/Flex';
import Text from '../Text/Text';
import Input from '../Input/Input';
import Button from '../Button/Button';
import {
  getRandomizerSettingsLocalStorage,
  setRandomizerSettingsLocalStorage,
} from '../../localStorage/randomizerSettingsLocalStorage';
import FormItem from '../FormItem/FormItem';
import { Controller, useForm } from 'react-hook-form';
import DiceIcon from '../../assets/Icons/DiceIcon.svg?react';
import { useDispatch } from 'react-redux';
import { setNeedRestartRandomizer } from '../../containers/App/actions';
import { useTranslation } from 'react-i18next';

type FormValues = {
  minRange: string;
  maxRange: string;
  delay: string;
};

type RandomizerFormProps = {
  embedded?: boolean;
};

const RandomizerForm = ({ embedded = false }: RandomizerFormProps) => {
  const { t } = useTranslation('translation');
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    values: getRandomizerSettingsLocalStorage(),
  });

  const onSubmit = handleSubmit(({ minRange, maxRange, delay }) => {
    setRandomizerSettingsLocalStorage(minRange, maxRange, delay);
    dispatch(setNeedRestartRandomizer(true));
  });

  const onReset = React.useCallback(
    (e: React.MouseEvent) => {
      reset({ minRange: '0', maxRange: '100', delay: '5' });
      e.preventDefault();
    },
    [reset],
  );

  const wrapperClass = embedded
    ? 'settings-panel RandomizerForm-wrapper'
    : 'RandomizerForm-wrapper SurfaceCard';

  const renderField = (
    label: string,
    name: keyof FormValues,
    rules: Record<string, unknown>,
    onChangeGuard: (value: string, onChange: (value: string) => void) => void,
  ) => (
    <div className="settings-field">
      {embedded ? (
        <p className="settings-field-label">{label}</p>
      ) : (
        <Text size={2} type="secondary">
          {label}
        </Text>
      )}
      <FormItem error={errors[name]?.message}>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field }) => (
            <Input
              placeholder={label}
              autoComplete="off"
              aria-invalid={errors[name]?.message ? true : false}
              {...field}
              onChange={(e) => onChangeGuard(e.target.value, field.onChange)}
            />
          )}
        />
      </FormItem>
    </div>
  );

  return (
    <Flex vertical gap={embedded ? 20 : 32} className={wrapperClass}>
      {embedded ? (
        <div className="settings-panel__head">
          <span className="settings-panel__icon">
            <DiceIcon />
          </span>
          <h2>{t('randomizer')}</h2>
        </div>
      ) : (
        <Flex align="center">
          <i className="RandomizerForm__Icon">
            <DiceIcon />
          </i>
          <Text size={4} bold>
            {t('randomizer')}
          </Text>
        </Flex>
      )}

      <form
        className={`RandomizerForm${embedded ? ' RandomizerForm--embedded' : ''}`}
        onSubmit={onSubmit}
      >
        {embedded ? (
          <div className="settings-fields-row">
            {renderField(
              t('range_from'),
              'minRange',
              { required: t('required') },
              (value, onChange) => {
                if (/^[0-9]*$/.test(value) && +value < 100) {
                  onChange(value);
                }
              },
            )}
            {renderField(
              t('range_to'),
              'maxRange',
              {
                required: t('required'),
                validate: (value: string) => {
                  if (+watch('minRange') >= +value) {
                    return t('max_range_must_be_greater_than_min_range');
                  }
                },
              },
              (value, onChange) => {
                if (/^[0-9]*$/.test(value) && +value <= 100) {
                  onChange(value);
                }
              },
            )}
            {renderField(
              t('frequency'),
              'delay',
              { required: t('required') },
              (value, onChange) => {
                const normalized = value.replace(/^0/, '');
                if (/^[0-9]*$/.test(normalized)) {
                  onChange(normalized);
                }
              },
            )}
          </div>
        ) : (
          <Flex gap={16}>
            <Flex vertical>
              <Text size={2} type="secondary">
                {t('range_from')}
              </Text>
              <FormItem error={errors.minRange?.message}>
                <Controller
                  name="minRange"
                  control={control}
                  rules={{ required: t('required') }}
                  render={({ field }) => (
                    <Input
                      placeholder={t('min')}
                      autoComplete="off"
                      aria-invalid={errors.minRange?.message ? true : false}
                      {...field}
                      onChange={(e) => {
                        if (/^[0-9]*$/.test(e.target.value) && +e.target.value < 100) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
              </FormItem>
            </Flex>
            <Flex vertical>
              <Text size={2} type="secondary">
                {t('range_to')}
              </Text>
              <FormItem error={errors.maxRange?.message}>
                <Controller
                  name="maxRange"
                  control={control}
                  rules={{
                    required: t('required'),
                    validate: (value) => {
                      if (+watch('minRange') >= +value) {
                        return t('max_range_must_be_greater_than_min_range');
                      }
                    },
                  }}
                  render={({ field }) => (
                    <Input
                      placeholder={t('max')}
                      autoComplete="off"
                      aria-invalid={errors.maxRange?.message ? true : false}
                      {...field}
                      onChange={(e) => {
                        if (/^[0-9]*$/.test(e.target.value) && +e.target.value <= 100) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
              </FormItem>
            </Flex>
            <div className="RandomizerForm--vertical-divider" />
            <Flex vertical>
              <Text size={2} type="secondary">
                {t('frequency')}
              </Text>
              <FormItem error={errors.delay?.message}>
                <Controller
                  name="delay"
                  control={control}
                  rules={{ required: t('required') }}
                  render={({ field }) => (
                    <Input
                      placeholder={t('delay')}
                      autoComplete="off"
                      aria-invalid={errors.delay?.message ? true : false}
                      {...field}
                      onChange={(e) => {
                        e.target.value = e.target.value.replace(/^0/, '');
                        if (/^[0-9]*$/.test(e.target.value)) {
                          field.onChange(e);
                        }
                      }}
                    />
                  )}
                />
              </FormItem>
            </Flex>
          </Flex>
        )}

        {embedded ? (
          <div className="settings-panel__footer">
            <button type="button" className="outline-button" onClick={onReset}>
              {t('reset')}
            </button>
            <button type="submit" className="acid-button">
              {t('apply')} <span aria-hidden="true">↗</span>
            </button>
          </div>
        ) : (
          <Flex justify="flex-end" gap={16}>
            <Button type="default" onClick={onReset}>
              {t('reset')}
            </Button>

            <FormItem>
              <Button type="primary">{t('apply')}</Button>
            </FormItem>
          </Flex>
        )}
      </form>
    </Flex>
  );
};

export default RandomizerForm;
